/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  ScrollView,
  TouchableOpacity,
  Platform,
  Dimensions,
  ActivityIndicator,
} from "react-native";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../store";
import LHOverlay from "../../../../components/LHOverlay";
import SignaturePad from "../../../../components/SignaturePad";
import {
  fetchTrainingContents,
  fetchUndertakingDetails,
} from "../../../../store/slices/employeeProfileSlice";
import { useToast } from "../../../../components/LhToastContainer";
import { createApiClient, apiClient } from "../../../../api/apiClient";
import { handleNHOErrorWithFriendlyMessage, NHO_API_NAMES } from '../../../../store/utils/nhoErrorHandler';

interface TrainingOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  title: string;
  content: React.ReactNode;
  employeeId?: string; // Optional employee ID for fetching content
  contentId?: string; // Optional content ID for fetching specific content
  onTrainingComplete?: (trainingTitle: string) => void; // Callback when training is completed
}

const TrainingOverlay: React.FC<TrainingOverlayProps> = ({
  visible,
  onDismiss,
  title,
  content,
  employeeId,
  onTrainingComplete,
}) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const [submittedBy, setSubmittedBy] = useState("");
  const [signatureFileName, setSignatureFileName] = useState("");
  const [signatureUrl, setSignatureUrl] = useState<string | undefined>(
    undefined
  );
  const [isSignatureUpdated, setIsSignatureUpdated] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  // States for API submission handling
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // States for PDF export functionality
  const [canExportPDF, setCanExportPDF] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // State to control signature pad edit functionality
  const [signaturesLocked, setSignaturesLocked] = useState(false);

  // For Manager signature (used only for Management Commitment)
  const [managerSignatureFileName, setManagerSignatureFileName] = useState("");
  const [managerSignatureUrl, setManagerSignatureUrl] = useState<
    string | undefined
  >(undefined);
  const [isManagerSignatureUpdated, setIsManagerSignatureUpdated] =
    useState(false);

  // Create scroll ref for scrolling to top
  const scrollRef = useRef<ScrollView>(null);

  const { isLoading, error, isDataFetched, undertakings } = useSelector(
    (state: RootState) => state.employeeProfile
  );

  // Check if this is the Management Commitment content
  const isManagementCommitment = title === "Management Commitment";

  // Helper to map content titles to Training_type values in the API
  const getTrainingTypeId = (title: string): number => {
    const trainingTypeMap: Record<string, number> = {
      "Safety Training Sign Off": 1,
      "PIT Training": 2,
      "VP Equipment": 3,
      "Metrics Explained": 4,
      "Performance Expectation": 5,
      "Management Commitment": 6,
    };
    return trainingTypeMap[title] || 0;
  };

  // Find the relevant undertaking record for this training type
  const trainingTypeId = getTrainingTypeId(title);
  const relevantUndertaking = undertakings.data?.find(
    (item) => item.Training_type === trainingTypeId
  );

  // Fetch training content when component mounts
  useEffect(() => {
    if (visible && employeeId && !isDataFetched) {
      dispatch(fetchTrainingContents(employeeId));
    }

    // Also dispatch fetchUndertakingDetails when component becomes visible
    if (visible) {
      console.log(
        "TrainingOverlay: Component became visible, fetching undertaking details"
      );
      dispatch(fetchUndertakingDetails())
        .then((response) => {
          if (response.payload && Array.isArray(response.payload)) {
            console.log(
              `TrainingOverlay: Fetched ${response.payload.length} undertaking records`
            );

            // Log available training types for debugging
            const trainingTypes = response.payload.map(
              (item) => item.Training_type
            );
            console.log(
              "TrainingOverlay: Available training types:",
              trainingTypes
            );

            // Check if we have a record matching our training type
            const record = response.payload.find(
              (item) => item.Training_type === trainingTypeId
            );
            if (record) {
              console.log(
                "TrainingOverlay: Found matching training type:",
                trainingTypeId
              );
              console.log("TrainingOverlay: Record data:", {
                emp_name: record.Emp_Name,
                created: record.Created,
                employee_sign: record.Employee_Sign ? "Present" : "Missing",
                manager_sign: record.Mng_Sign ? "Present" : "Missing",
              });
            } else {
              console.log(
                "TrainingOverlay: No matching training type found for:",
                trainingTypeId
              );
            }
          } else {
            console.warn(
              "TrainingOverlay: Unexpected response format for undertaking details:",
              response
            );
          }
        })
        .catch((error) => {
          console.error(
            "TrainingOverlay: Error fetching undertaking details:",
            error
          );

          // Show a non-blocking toast for the error but don't disrupt the UI
          showToast({
            header: "Data Load Error",
            subtext:
              "Could not load previous training records. You can still complete the form.",
            variant: "warning",
            position: "top",
          });
        });
    }
  }, [visible, employeeId, isDataFetched, dispatch, trainingTypeId]);

  // Update form fields when undertaking data is available
  useEffect(() => {
    if (relevantUndertaking) {
      console.log(
        "Found relevant undertaking for training type:",
        trainingTypeId,
        relevantUndertaking
      );

      // Set submitted by name from the API
      setSubmittedBy(relevantUndertaking.Emp_Name || "");

      // Set employee signature from API
      if (
        relevantUndertaking.Employee_Sign &&
        relevantUndertaking.Emp_Sign_Status === 1
      ) {
        console.log(
          "Setting employee signature from API:",
          relevantUndertaking.Employee_Sign
        );

        // Construct proper URL for the signature
        const employeeSignUrl = constructSignatureUrl(
          relevantUndertaking.Employee_Sign
        );
        console.log("Constructed employee signature URL:", employeeSignUrl);

        setSignatureUrl(employeeSignUrl);
        setSignatureFileName("signature-from-api.png");
        setIsSignatureUpdated(false); // Set to false to indicate this is from API, not a new upload
      }

      // Set manager signature for Management Commitment
      if (
        isManagementCommitment &&
        relevantUndertaking.Mng_Sign &&
        relevantUndertaking.Mng_Sign_Status === 1
      ) {
        console.log(
          "Setting manager signature from API:",
          relevantUndertaking.Mng_Sign
        );

        // Construct proper URL for the signature
        const managerSignUrl = constructSignatureUrl(
          relevantUndertaking.Mng_Sign
        );
        console.log("Constructed manager signature URL:", managerSignUrl);

        setManagerSignatureUrl(managerSignUrl);
        setManagerSignatureFileName("manager-signature-from-api.png");
        setIsManagerSignatureUpdated(false); // Set to false to indicate this is from API, not a new upload
      }

      // Mark form as submitted if we have data from API
      setIsFormSubmitted(true);
      setSubmitSuccess(true);
      console.log(
        "TrainingOverlay: Setting canExportPDF to true (from API data)"
      );
      console.log(
        "TrainingOverlay: relevantUndertaking data:",
        relevantUndertaking
      );
      setCanExportPDF(true);
      // Lock signatures for pre-existing data from API
      setSignaturesLocked(true);
    }
  }, [relevantUndertaking, trainingTypeId, isManagementCommitment]);

  // Current date in format DD/MM/YYYY HH:MM
  const currentDate = new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  // Get submission date from API or use current date as fallback
  const submissionDate = relevantUndertaking?.Created
    ? new Date(relevantUndertaking.Created).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : currentDate;

  // Function to handle clearing employee signature
  const handleClearSignature = useCallback(() => {
    console.log("TrainingOverlay: Clearing signature data");
    setSignatureFileName("");
    setSignatureUrl(undefined);
    setIsSignatureUpdated(false);
  }, []);

  // Function to handle clearing manager signature
  const handleClearManagerSignature = useCallback(() => {
    console.log("TrainingOverlay: Clearing manager signature data");
    setManagerSignatureFileName("");
    setManagerSignatureUrl(undefined);
    setIsManagerSignatureUpdated(false);
  }, []);

  // Helper function to construct a proper URL for API-provided signatures
  const constructSignatureUrl = useCallback(
    (signatureValue: string | undefined): string | undefined => {
      if (!signatureValue) {
        return undefined;
      }

      // If it's already a data URL, return as is
      if (signatureValue.startsWith("data:")) {
        return signatureValue;
      }

      // If it's a full URL, return as is
      if (signatureValue.startsWith("http")) {
        return signatureValue;
      }

      // Otherwise, assume it's a relative path and construct a full URL
      const apiBaseUrl = process.env.REACT_APP_API_URL || "";
      // Remove leading slash if present to avoid double slashes
      const cleanPath = signatureValue.startsWith("/")
        ? signatureValue.substring(1)
        : signatureValue;
      return `${apiBaseUrl}/common/images/${cleanPath}`;
    },
    []
  ); // No dependencies as we're just using environment variables

  // Function to format name properly (e.g., "Mohammed, Sohail - Contractor {}" -> "Sohail Mohammed")
  const formatName = useCallback((name: string | undefined): string => {
    if (!name) {
      return "Unknown User";
    }

    // Handle format like "Mohammed, Sohail - Contractor {}"
    if (name.includes(",")) {
      const parts = name.split(",");
      if (parts.length >= 2) {
        const lastName = parts[0].trim();
        // Remove anything after a dash or curly brace
        const firstNamePart = parts[1].split(/[-{]/)[0].trim();
        return `${firstNamePart} ${lastName}`;
      }
    }

    // Handle other formats or return as is if no comma
    return name.split(/[-{]/)[0].trim(); // Remove anything after dash or curly brace
  }, []);

  // Get auth user data and app data from Redux store
  const userData = useSelector((state: RootState) => state.auth.user);
  const { selectedLocation } = useSelector((state: RootState) => state.appData);
  const { periodWeek } = useSelector((state: RootState) => state.appData);

  // Get employee data from employeeUpdatesSlice
  const { firstName, lastName, gpid } = useSelector(
    (state: RootState) => state.employeeUpdates
  );

  // Get NHO data for PDF export
  const userEmail = useSelector(
    (state: RootState) => state.auth?.user?.username
  );
  const location = useSelector(
    (state: RootState) => state.appData?.selectedLocation?.displayText
  );

  // Define interface for the API payload
  interface UndertakingPayload {
    Emp_Name: string; // Formatted employee name (e.g., "Sohail Mohammed")
    GPID: string; // Employee ID
    Week: string; // Week number as string
    Period: number; // Period number
    Training_type: string; // Training type number as string format
    Employee_Sign: string; // Contains signatureUrl (not signatureFileName)
    Emp_Sign_Status: number; // 1 if signed, 0 if not signed
    Pepsi_Week: number; // Week number as numeric
    Mng_Sign: string; // Contains managerSignatureUrl (not managerSignatureFileName)
    Mng_Sign_Status: number; // 1 if signed by manager, 0 if not signed
    Mng_Name?: string; // Optional manager name (used only for Management Commitment)
  }

  const handleSubmit = async () => {
    // Check if signature is provided (primarily check URL since that's what we're sending to the API)
    if (!signatureUrl) {
      showToast({
        header: "Validation Error",
        subtext: "Signature is required",
        variant: "error",
        position: "top",
      });
      return;
    }

    // For management commitment, require both signatures (primarily check URLs)
    if (isManagementCommitment && !managerSignatureUrl) {
      showToast({
        header: "Validation Error",
        subtext: "Manager signature is required",
        variant: "error",
        position: "top",
      });
      return;
    }

    // Show loading state
    setIsFormSubmitted(true);
    setIsSubmitting(true);
    setSubmitSuccess(false);
    setSubmitError(null);
    // Lock signatures when form is submitted
    setSignaturesLocked(true);

    try {
      // Get training type ID using static mapping instead of content order
      let trainingTypeId = "1"; // Default ID if no match found

      // Define static mapping of training titles to their IDs as specified
      const trainingTypeMap: Record<string, string> = {
        "Safety Training Sign Off": "1",
        "PIT Training": "2",
        "VP Equipment": "3",
        "Metrics Explained": "4",
        "Performance Expectation": "5",
        "Management Commitment": "6",
      };

      // Check for exact match first
      if (trainingTypeMap[title]) {
        trainingTypeId = trainingTypeMap[title];
        console.log(
          "Found exact title match:",
          title,
          "with ID:",
          trainingTypeId
        );
      } else {
        // If no exact match, try to match by substring (case insensitive)
        const lowerTitle = title.toLowerCase();

        // Check for partial matches
        if (lowerTitle.includes("safety") && lowerTitle.includes("sign off")) {
          trainingTypeId = "1";
        } else if (
          lowerTitle.includes("pit") &&
          lowerTitle.includes("training")
        ) {
          trainingTypeId = "2";
        } else if (
          lowerTitle.includes("vp") &&
          lowerTitle.includes("equipment")
        ) {
          trainingTypeId = "3";
        } else if (lowerTitle.includes("metrics")) {
          trainingTypeId = "4";
        } else if (
          lowerTitle.includes("performance") &&
          lowerTitle.includes("expect")
        ) {
          trainingTypeId = "5";
        } else if (
          lowerTitle.includes("management") &&
          lowerTitle.includes("commit")
        ) {
          trainingTypeId = "6";
        }

        // If no partial match, use default (already set to 1)
        console.log(
          "No exact or partial match found, using default ID:",
          trainingTypeId
        );
      }

      console.log(
        "Using training type ID:",
        trainingTypeId,
        "for title:",
        title
      );

      // Format signature URL if needed (ensure it's a proper URL or relative path)
      const formatSignatureUrl = (url: string | undefined): string => {
        if (!url) {
          return "";
        }

        // If URL starts with data: (e.g., data:image/png), it's already a proper data URL
        if (url.startsWith("data:")) {
          return url;
        }

        // If URL is a file path without protocol, ensure it's properly formatted
        if (!url.includes("://") && !url.startsWith("/")) {
          return "/" + url;
        }

        return url;
      };

      const employeeSignUrl = formatSignatureUrl(signatureUrl);
      const managerSignUrl = formatSignatureUrl(managerSignatureUrl);

      // Prepare the API payload according to the required structure
      const payload: UndertakingPayload = {
        Emp_Name: `${firstName} ${lastName}`, // Using employeeUpdatesSlice data
        GPID: gpid || "0", // Using gpid from employeeUpdatesSlice instead of employeeId prop
        Week: periodWeek?.data?.week?.toString() || "0",
        Period: periodWeek?.data?.period || 0,
        Training_type: trainingTypeId, // Send as string but ensure it's a number
        Employee_Sign: employeeSignUrl,
        Emp_Sign_Status: signatureUrl ? 1 : 0,
        Pepsi_Week: periodWeek?.data?.week || 0,
        Mng_Sign: managerSignUrl,
        Mng_Sign_Status: isManagementCommitment && managerSignatureUrl ? 1 : 0,
      };

      // Add manager name only for Management Commitment
      if (isManagementCommitment) {
        payload.Mng_Name = formatName(userData?.name); // Using auth slice user data
      }

      console.log(
        "Submitting training data:",
        JSON.stringify(payload, null, 2)
      );
      console.log(
        "Employee Name (from employeeUpdatesSlice):",
        `${firstName} ${lastName}`
      );

      // Create API client and make the POST request
      const api = createApiClient();

      // Add specific content-type header for this request
      api.defaults.headers.post["Content-Type"] = "application/json";

      const response = await apiClient(
        api,
        "/new-hire-onboarding/undertaking",
        "POST",
        payload
      );

      console.log("API Response:", response);

      // Set submittedBy from the employee first name and last name
      setSubmittedBy(`${firstName} ${lastName}`);

      // Don't show success toast as we show completion in the title
      // showToast({
      //   header: 'Success',
      //   subtext: 'Training completed successfully',
      //   variant: 'success',
      //   position: 'top'
      // });

      // Set states to show the form with the completed data
      setSubmitSuccess(true); // Keep this for logic that depends on it
      console.log(
        "TrainingOverlay: Setting canExportPDF to true (after successful submission)"
      );
      setCanExportPDF(true); // Enable PDF export
      setIsSubmitting(false);
      setIsFormSubmitted(true); // Make sure form shows the submitted data
      // Ensure signatures remain locked after submission completes
      setSignaturesLocked(true);

      // Refresh undertaking details to get the latest data
      console.log("Refreshing undertaking details after successful submission");
      dispatch(fetchUndertakingDetails())
        .then((refreshResponse) => {
          console.log(
            "Undertaking details refreshed successfully",
            refreshResponse
          );

          // Call the onTrainingComplete callback if it exists
          if (onTrainingComplete) {
            console.log("Calling onTrainingComplete with title:", title);
            onTrainingComplete(title);
          }
        })
        .catch((refreshError) => {
          console.error("Failed to refresh undertaking details:", refreshError);
        });
    } catch (error) {
      console.error("Failed to submit training:", error);

      // Capture error message for local state
      let capturedErrorMessage = "Failed to submit training. Please try again.";
      const errorObj: any = error;
      if (errorObj && typeof errorObj === "object") {
        if ("message" in errorObj && errorObj.message) {
          capturedErrorMessage = String(errorObj.message);
        }
        if (errorObj.response?.data?.message) {
          capturedErrorMessage = String(errorObj.response.data.message);
        }
      }

      // Use centralized NHO error modal for API errors
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.SUBMIT_UNDERTAKING, error, true, { gpid, title });
      } catch (e) {
        console.error('Error while dispatching NHO centralized handler:', e);
      }

      // Set error state
      setSubmitError(capturedErrorMessage);
      setIsSubmitting(false);
      // Unlock signatures if there was an error during submission
      setSignaturesLocked(false);
    }
  };

  // Function to export PDF
  const exportToPDF = useCallback(async () => {
    console.log("=== PDF EXPORT DEBUG START ===");
    console.log("exportToPDF function called");
    console.log("canExportPDF:", canExportPDF);
    console.log("userEmail:", userEmail);
    console.log("gpid:", gpid);
    console.log("location:", location);
    console.log("trainingTypeId:", trainingTypeId);

    if (!canExportPDF) {
      console.log("PDF Export blocked: canExportPDF is false");
      showToast({
        header: "Export Not Available",
        subtext: "Please complete the training first before exporting.",
        variant: "warning",
        position: "top",
      });
      return;
    }

    // Validate required parameters
    if (!userEmail) {
      console.error("PDF Export Error: User email not found");
      scrollRef?.current?.scrollTo({ y: 0, animated: true });
      showToast({
        header: "PDF Export Failed",
        subtext: "User email not found. Please log in again.",
        variant: "error",
        position: "top",
      });
      return;
    }

    if (!gpid) {
      console.error("PDF Export Error: Employee ID (gpid) not found");
      scrollRef?.current?.scrollTo({ y: 0, animated: true });
      showToast({
        header: "PDF Export Failed",
        subtext:
          "Employee ID not found. Please ensure employee data is loaded.",
        variant: "error",
        position: "top",
      });
      return;
    }

    if (!location) {
      console.error("PDF Export Error: Location not found");
      scrollRef?.current?.scrollTo({ y: 0, animated: true });
      showToast({
        header: "PDF Export Failed",
        subtext: "Location not selected. Please select a location.",
        variant: "error",
        position: "top",
      });
      return;
    }

    console.log("All validations passed, proceeding with PDF export...");
    setIsExporting(true);

    try {
      console.log(`Exporting ${title} PDF for employee:`, gpid);
      console.log("PDF Export params:", {
        emailTo: userEmail,
        inspection_module: "nho_certificate",
        id: gpid,
        location: location,
        tt: trainingTypeId,
      });

      // Create API client
      const api = createApiClient();

      // Build URL with query parameters
      const url = `/notifications/nho/email/pdf?emailTo=${encodeURIComponent(
        userEmail
      )}&inspection_module=nho_certificate&id=${encodeURIComponent(
        gpid
      )}&location=${encodeURIComponent(location)}&tt=${encodeURIComponent(
        trainingTypeId
      )}`;

      console.log("Making API call to URL:", url);

      // Make API call to export PDF
      const response = await apiClient(api, url, "GET");

      console.log("PDF Export API Response:", response);

      // Scroll to top and show success message
      scrollRef?.current?.scrollTo({ y: 0, animated: true });

      showToast({
        header: "PDF Export Successful",
        subtext:
          response?.message || `${title} PDF export email sent successfully!`,
        variant: "success",
        position: "top",
      });
    } catch (error: any) {
      console.error(`${title} PDF Export Error:`, error);

      // Scroll to top for error display
      scrollRef?.current?.scrollTo({ y: 0, animated: true });

      // Handle different error types
      let errorMessage = `Failed to send ${title} PDF export email`;

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid request parameters";
      } else if (error.response?.status === 500) {
        errorMessage = "Server error occurred. Please try again later.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast({
        header: "PDF Export Failed",
        subtext: errorMessage,
        variant: "error",
        position: "top",
      });
    } finally {
      console.log("PDF export process completed, setting isExporting to false");
      setIsExporting(false);
      console.log("=== PDF EXPORT DEBUG END ===");
    }
  }, [
    canExportPDF,
    title,
    showToast,
    userEmail,
    gpid,
    location,
    trainingTypeId,
  ]);

  // Debug log Redux selector values
  useEffect(() => {
    console.log("=== TrainingOverlay Redux Values ===");
    console.log("userEmail:", userEmail);
    console.log("gpid:", gpid);
    console.log("location:", location);
    console.log("canExportPDF:", canExportPDF);
    console.log("submitSuccess:", submitSuccess);
    console.log("isFormSubmitted:", isFormSubmitted);
    console.log("trainingTypeId:", trainingTypeId);
    console.log("================================");
  }, [
    userEmail,
    gpid,
    location,
    canExportPDF,
    submitSuccess,
    isFormSubmitted,
    trainingTypeId,
  ]);

  // Form is valid when signature URL is provided (and manager signature URL for Management Commitment)
  const isFormValid =
    !!signatureUrl && (!isManagementCommitment || !!managerSignatureUrl);
  const isCompleteButtonDisabled = !isFormValid;

  // Render content based on loading state
  const renderContent = () => {
    if (isLoading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#002366" />
          <Text style={styles.loadingText}>Loading training content...</Text>
        </View>
      );
    }

    if (error) {
      return (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>
            Error loading training content: {error}
          </Text>
        </View>
      );
    }

    // Pass the submitSuccess state to the content component if possible
    if (React.isValidElement(content)) {
      // Type assertion to allow adding isSubmitSuccess prop
      return React.cloneElement(content as React.ReactElement<any>, {
        isSubmitSuccess: submitSuccess,
      });
    }

    return content;
  };

  // Render success message
  const renderSuccess = () => (
    <View style={styles.resultContainer}>
      {/* <View style={styles.successIconContainer}>
        <Text style={styles.successIcon}>✓</Text>
      </View>
      <Text style={styles.successTitle}>Training Completed Successfully</Text>
      <Text style={styles.successMessage}>
        The training has been successfully completed and recorded.
      </Text> */}
      <TouchableOpacity
        style={[styles.button, styles.closeButton]}
        onPress={onDismiss}
      >
        <Text style={styles.closeButtonText}>Close</Text>
      </TouchableOpacity>

      {/* Export button on the success screen */}
      <TouchableOpacity
        style={[styles.button, styles.exportButton, { marginTop: 5 }]}
        onPress={() => {
          console.log("Export PDF button pressed (success screen)");
          exportToPDF();
        }}
        disabled={isExporting}
      >
        {isExporting ? (
          <View style={styles.buttonContentRow}>
            <ActivityIndicator
              size="small"
              color="white"
              style={styles.buttonLoader}
            />
            <Text style={styles.exportButtonText}>Sending Email...</Text>
          </View>
        ) : (
          <Text style={styles.exportButtonText}>Export PDF</Text>
        )}
      </TouchableOpacity>
    </View>
  );

  // Render error message
  const renderError = () => (
    <View style={styles.resultContainer}>
      <View style={styles.errorIconContainer}>
        <Text style={styles.errorIcon}>✕</Text>
      </View>
      <Text style={styles.errorTitle}>Error Completing Training</Text>
      <Text style={styles.errorMessage}>
        {submitError ||
          "There was an error submitting the training. Please try again later."}
      </Text>
      <TouchableOpacity
        style={[styles.button, styles.closeButton]}
        onPress={onDismiss}
      >
        <Text style={styles.closeButtonText}>Close</Text>
      </TouchableOpacity>
    </View>
  );

  // Complete button with loading indicator
  const renderCompleteButton = (customStyles?: any) => (
    <TouchableOpacity
      style={[
        styles.button,
        styles.completeButton,
        customStyles,
        (isCompleteButtonDisabled || isSubmitting) && styles.disabledButton,
      ]}
      onPress={
        isCompleteButtonDisabled || isSubmitting ? undefined : handleSubmit
      }
      disabled={isCompleteButtonDisabled || isSubmitting}
    >
      {isSubmitting ? (
        <View style={styles.buttonContentRow}>
          <ActivityIndicator
            size="small"
            color="white"
            style={styles.buttonLoader}
          />
          <Text style={styles.completeButtonText}>Submitting...</Text>
        </View>
      ) : (
        <Text style={styles.completeButtonText}>Complete</Text>
      )}
    </TouchableOpacity>
  );

  // Reusable Submitted By and Date table component
  const renderSubmittedTable = () => {
    if (!isFormSubmitted) return null;
    
    return (
      <View style={[
        styles.formTable, 
        { 
          marginTop: 20, 
          // Apply responsive width and centering for non-management commitment
          ...(isManagementCommitment ? {} : { 
            width: isWideScreen ? "60%" : "80%", 
            alignSelf: "center" 
          })
        }
      ]}>
        <View style={styles.tableRow}>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>
              Submitted By
            </Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>
              Submitted Date
            </Text>
          </View>
        </View>
        <View style={styles.tableRow}>
          <View style={styles.tableCell}>
            <Text style={styles.dateText}>{submittedBy}</Text>
          </View>
          <View style={styles.tableCell}>
            <Text style={styles.dateText}>{submissionDate}</Text>
          </View>
        </View>
      </View>
    );
  };

  // Modify title to show completion status when submitSuccess is true
  const displayTitle = submitSuccess ? `${title} - Completed` : title;

  return (
    <LHOverlay
      visible={visible}
      onDismiss={onDismiss}
      title={displayTitle}
      size="large"
    >
      <ScrollView ref={scrollRef} style={styles.scrollContainer}>
        <View style={styles.contentContainer}>
          {/* Show error screen if there's an error, otherwise show the form regardless of submission status */}
          {submitError ? (
            renderError()
          ) : (
            <>
              {renderContent()}

              <View style={styles.formSection}>
                {isManagementCommitment ? (
                  <>
                    <View style={styles.signatureRow}>
                      <View style={styles.signatureColumnHalf}>
                        <Text style={styles.sectionTitle}>
                          Employee Signature
                        </Text>
                        <View style={styles.signaturePadContainer}>
                          <SignaturePad
                            onOK={(fileName: string, imageUrl?: string) => {
                              console.log(
                                "TrainingOverlay: Received signature data - fileName:",
                                fileName,
                                "imageUrl:",
                                imageUrl
                              );
                              setSignatureFileName(fileName);
                              setSignatureUrl(imageUrl);
                              setIsSignatureUpdated(true);
                            }}
                            onClear={handleClearSignature}
                            initialImageUrl={signatureUrl}
                            disabled={
                              signaturesLocked || submitSuccess || isSubmitting
                            }
                            containerName="nho"
                          />
                          {signatureFileName && (
                            <Text style={styles.signatureText}>
                              Signature uploaded successfully
                            </Text>
                          )}
                        </View>
                      </View>

                      <View style={styles.signatureColumnHalf}>
                        <Text style={styles.sectionTitle}>
                          Manager Signature
                        </Text>
                        <View style={styles.signaturePadContainer}>
                          <SignaturePad
                            onOK={(fileName: string, imageUrl?: string) => {
                              console.log(
                                "TrainingOverlay: Received manager signature data - fileName:",
                                fileName,
                                "imageUrl:",
                                imageUrl
                              );
                              setManagerSignatureFileName(fileName);
                              setManagerSignatureUrl(imageUrl);
                              setIsManagerSignatureUpdated(true);
                            }}
                            onClear={handleClearManagerSignature}
                            initialImageUrl={managerSignatureUrl}
                            disabled={
                              signaturesLocked || submitSuccess || isSubmitting
                            }
                            containerName="nho"
                          />
                          {managerSignatureFileName && (
                            <Text style={styles.signatureText}>
                              Manager signature uploaded successfully
                            </Text>
                          )}
                        </View>
                      </View>
                    </View>

                {/* Submitted By and Date table - shown after signature sections when form is submitted */}
                {renderSubmittedTable()}


                    {/* Message instructing users to sign - only show when button is disabled and form is not submitted successfully */}
                    {isCompleteButtonDisabled && !submitSuccess && (
                      <View style={styles.instructionContainer}>
                        <Text style={styles.instructionText}>
                          Please sign in both signature fields to complete this
                          form
                        </Text>
                      </View>
                    )}

                    {/* Buttons below both signatures with more spacing */}
                    <View
                      style={[styles.buttonRowContainer, { marginTop: 30 }]}
                    >
                      {/* Show Complete button only if not already submitted successfully */}
                      {!submitSuccess ? (
                        // Show Complete and Cancel buttons when not submitted
                        <>
                          {renderCompleteButton(styles.buttonHalf)}

                          <TouchableOpacity
                            style={[
                              styles.button,
                              styles.cancelButton,
                              styles.buttonHalf,
                            ]}
                            onPress={onDismiss}
                            disabled={isSubmitting}
                          >
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                          </TouchableOpacity>
                        </>
                      ) : (
                        // Show Cancel and Export PDF when submitted successfully
                        <>
                          <TouchableOpacity
                            style={[
                              styles.button,
                              styles.cancelButton,
                              styles.buttonHalf,
                            ]}
                            onPress={onDismiss}
                          >
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                          </TouchableOpacity>

                          {canExportPDF && (
                            <TouchableOpacity
                              style={[
                                styles.button,
                                styles.exportButton,
                                styles.buttonHalf,
                              ]}
                              onPress={() => {
                                console.log(
                                  "Export PDF button pressed (management commitment)"
                                );
                                exportToPDF();
                              }}
                              disabled={isExporting}
                            >
                              {isExporting ? (
                                <View style={styles.buttonContentRow}>
                                  <ActivityIndicator
                                    size="small"
                                    color="white"
                                    style={styles.buttonLoader}
                                  />
                                  <Text style={styles.exportButtonText}>
                                    Sending Email...
                                  </Text>
                                </View>
                              ) : (
                                <Text style={styles.exportButtonText}>
                                  Export PDF
                                </Text>
                              )}
                            </TouchableOpacity>
                          )}
                        </>
                      )}
                    </View>
                  </>
                ) : (
                  /* Updated layout for non-Management Commitment content - using similar structure to Management Commitment */
                  <>
                    <View style={styles.nonManagementContainer}>
                      {/* Title on left, signature pad centered */}
                      <View style={styles.signatureTitleContainer}>
                        <Text style={styles.sectionTitle}>
                          Employee Signature
                        </Text>
                      </View>

                      {/* Centered Signature Pad - with fixed width to ensure proper centering */}
                      <View style={styles.centeredSignaturePad}>
                        <View
                          style={[
                            styles.signaturePadContainer,
                            { width: isWideScreen ? "60%" : "80%" },
                          ]}
                        >
                          <SignaturePad
                            onOK={(fileName: string, imageUrl?: string) => {
                              console.log(
                                "TrainingOverlay: Received signature data - fileName:",
                                fileName,
                                "imageUrl:",
                                imageUrl
                              );
                              // Store signature data in local state
                              setSignatureFileName(fileName);
                              setSignatureUrl(imageUrl);
                              setIsSignatureUpdated(true);

                              console.log(
                                "TrainingOverlay: Updated signature data"
                              );
                            }}
                            onClear={handleClearSignature}
                            initialImageUrl={signatureUrl}
                            disabled={
                              signaturesLocked || submitSuccess || isSubmitting
                            }
                            containerName="nho"
                          />
                          {signatureFileName && (
                            <Text style={styles.signatureText}>
                              Signature uploaded successfully
                            </Text>
                          )}
                        </View>
                      </View>
                    </View>

                {/* Submitted By and Date table - shown after signature sections when form is submitted */}
                {renderSubmittedTable()}


                    {/* Message instructing users to sign - only show when button is disabled and form is not submitted successfully */}
                    {isCompleteButtonDisabled && !submitSuccess && (
                      <View style={styles.instructionContainer}>
                        <Text style={styles.instructionText}>
                          Please sign to complete this form
                        </Text>
                      </View>
                    )}

                    {/* Buttons below signature like Management Commitment - in a centered container */}
                    <View
                      style={[
                        styles.buttonRowContainer,
                        {
                          marginTop: 30,
                          width: isWideScreen ? "60%" : "80%",
                          alignSelf: "center",
                        },
                      ]}
                    >
                      {/* Show Complete button only if not already submitted successfully */}
                      {!submitSuccess ? (
                        // Show Complete and Cancel buttons when not submitted
                        <>
                          {renderCompleteButton(styles.buttonHalf)}

                          <TouchableOpacity
                            style={[
                              styles.button,
                              styles.cancelButton,
                              styles.buttonHalf,
                            ]}
                            onPress={onDismiss}
                            disabled={isSubmitting}
                          >
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                          </TouchableOpacity>
                        </>
                      ) : (
                        // Show Close and Export PDF when submitted successfully
                        <>
                          <TouchableOpacity
                            style={[
                              styles.button,
                              styles.cancelButton,
                              styles.buttonHalf,
                            ]}
                            onPress={onDismiss}
                          >
                            <Text style={styles.cancelButtonText}>Close</Text>
                          </TouchableOpacity>

                          {canExportPDF && (
                            <TouchableOpacity
                              style={[
                                styles.button,
                                styles.exportButton,
                                styles.buttonHalf,
                              ]}
                              onPress={() => {
                                console.log(
                                  "Export PDF button pressed (non-management)"
                                );
                                exportToPDF();
                              }}
                              disabled={isExporting}
                            >
                              {isExporting ? (
                                <View style={styles.buttonContentRow}>
                                  <ActivityIndicator
                                    size="small"
                                    color="white"
                                    style={styles.buttonLoader}
                                  />
                                  <Text style={styles.exportButtonText}>
                                    Sending Email...
                                  </Text>
                                </View>
                              ) : (
                                <Text style={styles.exportButtonText}>
                                  Export PDF
                                </Text>
                              )}
                            </TouchableOpacity>
                          )}
                        </>
                      )}
                    </View>
                  </>
                )}
              </View>
            </>
          )}
        </View>
      </ScrollView>
    </LHOverlay>
  );
};

// Get screen width for responsive layout
const { width } = Dimensions.get("window");
const isWeb = Platform.OS === "web";
const isWideScreen = width > 768;

const styles = StyleSheet.create({
  submissionSuccessIndicator: {
    backgroundColor: "#e6f7e6",
    padding: 10,
    borderRadius: 5,
    marginBottom: 10,
    borderLeftWidth: 4,
    borderLeftColor: "#4CAF50",
    flexDirection: "row",
    alignItems: "center",
  },
  submissionSuccessText: {
    color: "#2E7D32",
    fontWeight: "bold",
    fontSize: 16,
  },
  // submissionSuccessIndicator: {
  //   backgroundColor: '#e6f7e6',
  //   padding: 10,
  //   borderRadius: 5,
  //   marginBottom: 10,
  //   borderLeftWidth: 4,
  //   borderLeftColor: '#4CAF50',
  //   flexDirection: 'row',
  //   alignItems: 'center',
  // },
  // submissionSuccessText: {
  //   color: '#2E7D32',
  //   fontWeight: 'bold',
  //   fontSize: 16,
  // },
  scrollContainer: {
    maxHeight: 600,
  },
  contentContainer: {
    paddingVertical: 10,
  },
  loadingContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    height: 200,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: "#002366",
  },
  errorContainer: {
    padding: 20,
    backgroundColor: "#fff8f8",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#ffdddd",
    marginBottom: 20,
  },
  errorText: {
    color: "#dc3545",
    fontSize: 16,
  },
  formSection: {
    marginTop: 20,
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
    paddingTop: 20,
  },
  formTable: {
    borderWidth: 1,
    borderColor: "#002366",
    marginBottom: 20,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableHeaderCell: {
    flex: 1,
    backgroundColor: "#002366",
    padding: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  tableHeaderText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  tableCell: {
    flex: 1,
    padding: 10,
    borderWidth: 0.5,
    borderColor: "#ccc",
    minHeight: 48,
    justifyContent: "center",
  },
  input: {
    height: 40,
    backgroundColor: "#fff",
    paddingHorizontal: 10,
  },
  dateText: {
    fontSize: 16,
    textAlign: "center",
  },
  // Responsive layout container
  actionsContainer: {
    flexDirection: isWeb && isWideScreen ? "row" : "column",
    marginVertical: 20,
    justifyContent: "flex-start",
    alignItems: isWeb && isWideScreen ? "flex-start" : "stretch",
  },
  // Button section styles
  buttonsSection: {
    flex: isWeb && isWideScreen ? 1 : undefined,
    marginRight: isWeb && isWideScreen ? 20 : 0,
    marginBottom: isWeb && isWideScreen ? 0 : 20,
    justifyContent: isWeb && isWideScreen ? "flex-start" : "center",
    alignSelf: isWeb && isWideScreen ? "flex-start" : "stretch",
    maxWidth: isWeb && isWideScreen ? 300 : undefined,
  },
  // Button section styles for right side placement with lower positioning
  buttonsSectionRight: {
    flex: 1,
    justifyContent: "flex-end", // Change to flex-end to push buttons lower
    alignSelf: "flex-start",
    paddingTop: 200, // Increase padding to position buttons even lower
    paddingLeft: 20,
    maxWidth: 300,
  },
  button: {
    height: 40,
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 12,
    paddingHorizontal: 20,
    width: isWeb && isWideScreen ? "100%" : undefined,
  },
  completeButton: {
    backgroundColor: "#002366",
  },
  completeButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "bold",
  },
  cancelButton: {
    backgroundColor: "#f0f0f0",
    borderWidth: 1,
    borderColor: "#ccc",
  },
  cancelButtonText: {
    color: "#333",
    fontSize: 16,
  },
  disabledButton: {
    backgroundColor: "#cccccc",
    opacity: 0.7,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#002366",
    marginBottom: 15,
  },
  validationMessage: {
    color: "#dc3545",
    fontSize: 14,
    marginTop: 10,
    textAlign: "center",
  },
  // Signature section styles
  signatureSection: {
    flex: 2,
    width: isWeb && isWideScreen ? "60%" : undefined,
    alignSelf: isWeb && isWideScreen ? "flex-start" : "stretch",
    maxWidth: isWeb && isWideScreen ? 600 : undefined,
  },
  signatureHeader: {
    backgroundColor: "#002366",
    padding: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  signatureHeaderText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  signaturePadContainer: {
    borderWidth: 1,
    borderColor: "#ccc",
    minHeight: 200,
    backgroundColor: "white",
    padding: 10,
  },
  signatureText: {
    marginTop: 8,
    fontSize: 14,
    color: "#495057",
    fontWeight: "500",
  },
  // Manager signature section styles
  managerSignatureSection: {
    marginTop: 30,
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
    paddingTop: 20,
  },
  managerSignatureHeader: {
    backgroundColor: "#002366",
    padding: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  managerSignatureHeaderText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  // Management Commitment specific styles
  signatureRow: {
    flexDirection: isWeb || isWideScreen ? "row" : "column",
    justifyContent: "space-between",
    marginVertical: 15,
  },
  signatureColumnHalf: {
    flex: 1,
    marginHorizontal: isWeb || isWideScreen ? 5 : 0,
    marginBottom: isWeb || isWideScreen ? 0 : 20,
    width: isWeb || isWideScreen ? "48%" : "100%",
  },
  buttonRowContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 20,
    paddingHorizontal: 10,
  },
  buttonHalf: {
    flex: 1,
    marginHorizontal: 10,
    height: 50,
  },
  // Keep these for backward compatibility
  formRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 15,
    paddingHorizontal: 5,
  },
  label: {
    width: 120,
    fontWeight: "bold",
    fontSize: 16,
  },
  dateContainer: {
    flex: 1,
    height: 40,
    justifyContent: "center",
  },
  // Added styles for name input
  nameInputContainer: {
    marginVertical: 15,
    width: "100%",
  },
  nameInput: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 4,
  },
  instructionContainer: {
    marginTop: 20,
    marginBottom: 10,
    alignItems: "center",
    paddingHorizontal: 20,
  },
  instructionText: {
    fontSize: 14,
    color: "#555",
    textAlign: "center",
    fontStyle: "italic",
  },
  // Success and error message styles
  resultContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 30,
    paddingTop: 50,
    paddingBottom: 50,
  },
  successIconContainer: {
    backgroundColor: "#4CAF50",
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  successIcon: {
    color: "white",
    fontSize: 40,
    fontWeight: "bold",
  },
  successTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#4CAF50",
    marginBottom: 15,
    textAlign: "center",
  },
  successMessage: {
    fontSize: 16,
    color: "#555",
    textAlign: "center",
    marginBottom: 30,
    maxWidth: "80%",
  },
  errorIconContainer: {
    backgroundColor: "#F44336",
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  errorIcon: {
    color: "white",
    fontSize: 40,
    fontWeight: "bold",
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#F44336",
    marginBottom: 15,
    textAlign: "center",
  },
  errorMessage: {
    fontSize: 16,
    color: "#555",
    textAlign: "center",
    marginBottom: 30,
    maxWidth: "80%",
  },
  closeButton: {
    backgroundColor: "#002366",
    width: 200,
    height: 50,
  },
  closeButtonText: {
    color: "white",
    fontSize: 18,
    fontWeight: "bold",
  },
  exportButton: {
    backgroundColor: "#4CAF50",

    height: 50, // Make sure it's the same height as the cancel button
  },
  exportButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "500",
  },
  buttonContentRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  buttonLoader: {
    marginRight: 10,
  },
  // Styles for non-Management Commitment sections
  nonManagementContainer: {
    flexDirection: "column",
    marginVertical: 15,
    width: "100%",
  },
  signatureTitleContainer: {
    alignSelf: "flex-start",
    marginBottom: 10,
    paddingLeft: 10,
  },
  centeredSignaturePad: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    marginTop: 10,
    marginBottom: 20,
  },
});

export default TrainingOverlay;
